import { CoreEvent, eventNames } from '@carvana/core-events';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { useCallback, useContext, useState } from 'react';
import { useUserData } from '~/contexts/UserContext';
import {
  addFavoriteApi,
  type FavoriteRequestBody,
  type FavoriteVehicle,
  fetchFavoritesApi,
  removeFavoriteApi
} from '~/services/favoritesService';
import { toError } from '~/utilities/errorHandlers';

export default function useLoadableFavorites() {
  const { trackEvent } = useContext(AnalyticsContext);
  const { isLoggedIn } = useUserData();

  const [fetchFavoritesTrigger, setFetchFavoritesTrigger] = useState(0);

  const {
    data: favorites,
    setData,
    isLoading,
    setLoading,
    setError
  } = useLoadableState<FavoriteVehicle[]>([]);

  const fetchFavorites = useCallback(
    async (controller: AbortController) => {
      const data = await fetchFavoritesApi(controller);
      setData(data?.hasHadFavorites ? data.vehicles : []);
    },
    [setData]
  );

  const triggerFetchFavorites = useCallback(() => {
    setFetchFavoritesTrigger(Date.now());
  }, []);

  const addFavorite = useCallback(
    async (payload: FavoriteRequestBody) => {
      setLoading();
      try {
        const newFavorite = await addFavoriteApi(payload);
        // A car can return a null response even if it was added because
        // (rarely) VDP or Discounts times out and then the vehicle returned may
        // be null if the VDP/Discount info was not added. But it is in the
        // database. If it wasn't added in the database it will return an error.
        if (newFavorite && payload.vehicleId === newFavorite.vehicleId) {
          setData([newFavorite, ...favorites]);
        } else {
          triggerFetchFavorites();
        }
        trackEvent('favorite added', { vehicleId: payload.vehicleId });
      } catch (err) {
        setError(toError(err));
      }
    },
    [
      favorites,
      setData,
      setError,
      setLoading,
      trackEvent,
      triggerFetchFavorites
    ]
  );

  const removeFavorite = useCallback(
    async (payload: FavoriteRequestBody) => {
      await removeFavoriteApi(payload);
      trackEvent('favorite removed', { vehicleId: payload.vehicleId });
      setData(
        favorites.filter(favorite => favorite.vehicleId !== payload.vehicleId)
      );
    },
    [favorites, setData, trackEvent]
  );

  useEffectWithController(
    controller => {
      if (!isLoggedIn) return;

      void fetchFavorites(controller);
    },
    [fetchFavorites, fetchFavoritesTrigger, isLoggedIn]
  );

  useEffectWithController(
    controller => {
      function handleFetchFavorites() {
        void fetchFavorites(controller);
      }

      CoreEvent.on(eventNames.favorites.vehicle.removed, handleFetchFavorites);

      return () =>
        CoreEvent.off(
          eventNames.favorites.vehicle.removed,
          handleFetchFavorites
        );
    },
    [fetchFavorites]
  );

  return {
    addFavorite,
    favorites,
    isLoading,
    removeFavorite,
    triggerFetchFavorites
  };
}
